import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

const purifyConfig = {
  USE_PROFILES: { html: true },
  ADD_TAGS: ['img', 'a', 'br'],
  ADD_ATTR: ['href', 'src', 'alt', 'title', 'width', 'height', 'target', 'rel'],
};

function removeEmptyQuillParagraphs(html) {
  if (!html) return '';

  return html
    .replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
}

function getLinkDisplayText(url) {
  try {
    const parsed = new URL(url);
    return parsed.hostname;
  } catch {
    return url;
  }
}

function linkifyTextNodes(html) {
  if (!html || typeof window === 'undefined') {
    return html || '';
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const urlRegex = /https?:\/\/[^\s<>"']+/g;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!node.nodeValue || !urlRegex.test(node.nodeValue)) {
          urlRegex.lastIndex = 0;
          return NodeFilter.FILTER_REJECT;
        }

        urlRegex.lastIndex = 0;

        if (node.parentElement?.closest('a')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const textNodes = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue;
    const fragment = document.createDocumentFragment();

    let lastIndex = 0;
    let match;

    urlRegex.lastIndex = 0;

    while ((match = urlRegex.exec(text)) !== null) {
      const fullUrl = match[0];

      if (match.index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, match.index))
        );
      }

      const link = document.createElement('a');
      link.href = fullUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = getLinkDisplayText(fullUrl);

      fragment.appendChild(link);

      lastIndex = match.index + fullUrl.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return container.innerHTML;
}

export default function CustomDescription({ html }) {
  const cleanHtml = useMemo(() => {
    const withoutEmptyParagraphs = removeEmptyQuillParagraphs(html || '');

    const sanitizedBeforeLinkify = DOMPurify.sanitize(
      withoutEmptyParagraphs,
      purifyConfig
    );

    const linkedHtml = linkifyTextNodes(sanitizedBeforeLinkify);

    const normalizedHtml = removeEmptyQuillParagraphs(linkedHtml);

    return DOMPurify.sanitize(normalizedHtml, purifyConfig);
  }, [html]);

  return (
    <div
      className="custom-description"
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
    />
  );
}
